import { useRouter } from 'expo-router';
import {
  Bug,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  Heart,
  HelpCircle,
  Home as HomeIcon,
  Lightbulb,
  MailCheck,
  MessageCircle,
  Package2,
  Rocket,
  Send,
  ShieldCheck,
  Sparkles,
} from 'lucide-react-native';
import { ComponentType, useMemo, useState } from 'react';
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { TurnstileGate, resetTurnstile } from '@/components/TurnstileGate';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { getVersionInfo } from '@/lib/app-updates';
import { sendContactForm } from '@/lib/contact-form';
import { webTransition } from '@/lib/hover-style';
import { goBackOrTo, goTo } from '@/lib/nav';
import { webNoOutline } from '@/lib/web-style';

/**
 * Contact & support page.
 *
 * Designed to feel warm, not corporate. The user reaches it from the
 * Tools menu when something isn't working or they want to send
 * feedback. Three sections:
 *
 *   1. Contact form — name + email + topic + message. On submit, opens
 *      the OS mail client pre-addressed with the support team's inbox
 *      and the form's contents in the body. The support address is
 *      intentionally NOT displayed on the page so users go through the
 *      form rather than bypassing it.
 *   2. What's coming — small roadmap teaser (multi-home, cloud sync).
 *   3. App info — version stamp + bundle id for support troubleshooting.
 *      The mail body includes the same version line automatically so
 *      we don't have to ask "what version are you on?" when we reply.
 */

const SUPPORT_INBOX = 'support@smartsiteservices.com';

type Topic = 'bug' | 'question' | 'idea' | 'other';

const TOPICS: { key: Topic; label: string; Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> }[] = [
  { key: 'bug',      label: 'Something broke',  Icon: Bug },
  { key: 'question', label: 'A question',       Icon: HelpCircle },
  { key: 'idea',     label: 'An idea',          Icon: Lightbulb },
  { key: 'other',    label: 'Something else',   Icon: MessageCircle },
];

const TOPIC_SUBJECT: Record<Topic, string> = {
  bug: 'Bug report',
  question: 'Question',
  idea: 'Feature idea',
  other: 'Hello',
};

const PLACEHOLDER_BY_TOPIC: Record<Topic, string> = {
  bug: 'What happened? What were you doing right before it broke?',
  question: 'What are you trying to do? We’ll point you the right way.',
  idea: 'Tell us what you’d love to see in Homestead.',
  other: 'Whatever’s on your mind — we read every note.',
};

export default function SupportScreen() {
  const router = useRouter();
  const version = getVersionInfo();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [topic, setTopic] = useState<Topic>('question');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  // Cloudflare Turnstile (web only) — null until the widget issues a
  // token. Native never gets one and is exempt server-side.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // All four contact fields are now required so the support team can
  // reach the user back over either channel and triage faster. The
  // email / phone regex checks are intentionally loose — we want to
  // catch obvious typos ("foo@bar" missing the TLD, "555" missing
  // digits) but not lock out international formats or unusual TLDs.
  const emailLooksValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    [email]
  );
  const phoneLooksValid = useMemo(() => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 7; // any reasonable local-or-international number
  }, [phone]);
  const canSubmit = useMemo(
    () =>
      name.trim().length > 0 &&
      emailLooksValid &&
      phoneLooksValid &&
      message.trim().length > 5 &&
      (Platform.OS !== 'web' || captchaToken != null),
    [name, emailLooksValid, phoneLooksValid, message, captchaToken]
  );

  // A dead disabled button doesn't tell anyone WHY it's dead. Tapping
  // Send while incomplete flips this on: missing fields get a clay
  // border and a plain-words line under the button says what's left.
  const [showHints, setShowHints] = useState(false);
  const missing = useMemo(() => {
    const m: string[] = [];
    if (!name.trim()) m.push('your name');
    if (!emailLooksValid) m.push('a valid email');
    if (!phoneLooksValid) m.push('a phone number');
    if (message.trim().length <= 5) m.push('a few words about what’s going on');
    if (Platform.OS === 'web' && captchaToken == null) m.push('the “Verify you are human” check');
    return m;
  }, [name, emailLooksValid, phoneLooksValid, message, captchaToken]);

  const [deliveryMode, setDeliveryMode] = useState<'server' | 'mailto'>('server');

  const onSubmit = async () => {
    if (sending) return;
    if (!canSubmit) {
      setShowHints(true);
      return;
    }
    setSending(true);
    const versionLine = `v${version.runtimeVersion} · ${version.channel} · ${version.bundleId}`;

    // First try the Supabase edge function → Mailgun pipeline. This
    // sends directly without ever opening the user's mail client, so
    // the support inbox stays invisible to the user. The function
    // address itself is only known to the server.
    const res = await sendContactForm({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      topic: TOPIC_SUBJECT[topic],
      message: message.trim(),
      version: versionLine,
      turnstile: captchaToken ?? undefined,
      platform: Platform.OS,
    });

    if (res.ok) {
      setDeliveryMode('server');
      setSubmitted(true);
      setSending(false);
      return;
    }

    // Turnstile tokens are single-use — a failed send consumed this
    // one, so reset the widget before any retry.
    setCaptchaToken(null);
    resetTurnstile();

    // Server delivery failed (function not deployed yet, Mailgun
    // outage, network hiccup). Fall back to a mailto handoff so the
    // user can still reach us — the inbox address shows up in their
    // mail client at that point, but never on the page itself.
    console.warn('Contact form server delivery failed, falling back to mailto', res.error);
    const subject = `Homestead — ${TOPIC_SUBJECT[topic]} from ${name.trim()}`;
    const body = [
      `Name:    ${name.trim()}`,
      `Email:   ${email.trim()}`,
      `Phone:   ${phone.trim()}`,
      `Topic:   ${TOPIC_SUBJECT[topic]}`,
      '',
      'Message:',
      message.trim(),
      '',
      `— sent from Homestead ${versionLine}`,
    ].join('\n');
    const url = `mailto:${SUPPORT_INBOX}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    try {
      await Linking.openURL(url);
    } catch {
      // Some environments (locked-down desktop browsers without a
      // configured mail client) silently no-op on mailto. The success
      // state's copy explains what to do.
    }
    setDeliveryMode('mailto');
    setSubmitted(true);
    setSending(false);
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setTopic('question');
    setMessage('');
    setSubmitted(false);
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <View style={styles.topbarLeft}>
          <Pressable
            onPress={() => goBackOrTo(router, '/(tabs)')}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Home"
            onPress={() => goTo(router, '/(tabs)')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <HomeIcon size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Schedule"
            onPress={() => goTo(router, '/(tabs)/schedule')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <Calendar size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
        </View>
        <Text style={styles.topbarTitle}>Support</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 60 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={styles.h1}>How can we help?</Text>
          <Text style={styles.sub}>
            Tell us what&apos;s going on — real humans read every message
            and aim to reply within a day or two.
          </Text>
        </View>

        <Text style={styles.sectionLabel}>GET IN TOUCH</Text>

        {submitted ? (
          <SuccessCard mode={deliveryMode} onReset={resetForm} />
        ) : (
          <View style={styles.formCard}>
            <Text style={styles.fieldLabel}>YOUR NAME</Text>
            <TextInput
              value={name}
              onChangeText={setName}
              placeholder="Sarah"
              placeholderTextColor={Palette.inkFaint}
              autoCorrect={false}
              style={[styles.input, webNoOutline, showHints && !name.trim() && styles.inputInvalid]}
            />

            {/*
              Email + phone share a row. On a narrow phone the two flex:1
              columns still give each input ~140px of width, which is
              enough for the placeholder to read. We collect both so the
              support team can follow up over whichever channel the user
              prefers and so we have a way to reach them if email bounces.
            */}
            <View style={styles.fieldRow}>
              <View style={styles.fieldRowCol}>
                <Text style={styles.fieldLabel}>YOUR EMAIL</Text>
                <TextInput
                  value={email}
                  onChangeText={setEmail}
                  placeholder="you@example.com"
                  placeholderTextColor={Palette.inkFaint}
                  autoCorrect={false}
                  autoCapitalize="none"
                  inputMode="email"
                  keyboardType="email-address"
                  style={[styles.input, webNoOutline, showHints && !emailLooksValid && styles.inputInvalid]}
                />
              </View>
              <View style={styles.fieldRowCol}>
                <Text style={styles.fieldLabel}>YOUR PHONE</Text>
                <TextInput
                  value={phone}
                  onChangeText={setPhone}
                  placeholder="(555) 123-4567"
                  placeholderTextColor={Palette.inkFaint}
                  autoCorrect={false}
                  inputMode="tel"
                  keyboardType="phone-pad"
                  style={[styles.input, webNoOutline, showHints && !phoneLooksValid && styles.inputInvalid]}
                />
              </View>
            </View>

            <Text style={styles.fieldLabel}>WHAT&apos;S THIS ABOUT?</Text>
            <View style={styles.topicRow}>
              {TOPICS.map((t) => {
                const sel = topic === t.key;
                return (
                  <Pressable
                    key={t.key}
                    onPress={() => setTopic(t.key)}
                    accessibilityLabel={t.label}
                    style={({ pressed }) => [
                      styles.topicChip,
                      sel && styles.topicChipSel,
                      pressed && { opacity: 0.9 },
                    ]}>
                    <t.Icon
                      size={13}
                      color={sel ? Palette.cream : Palette.forest}
                      strokeWidth={2}
                    />
                    <Text
                      style={[
                        styles.topicChipText,
                        sel && styles.topicChipTextSel,
                      ]}>
                      {t.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Text style={styles.fieldLabel}>YOUR MESSAGE</Text>
            <TextInput
              value={message}
              onChangeText={setMessage}
              placeholder={PLACEHOLDER_BY_TOPIC[topic]}
              placeholderTextColor={Palette.inkFaint}
              multiline
              style={[
                styles.input,
                styles.inputMultiline,
                webNoOutline,
                showHints && message.trim().length <= 5 && styles.inputInvalid,
              ]}
            />

            {Platform.OS === 'web' ? (
              <TurnstileGate onToken={setCaptchaToken} />
            ) : null}

            <Pressable
              onPress={onSubmit}
              disabled={sending}
              accessibilityLabel="Send message"
              style={({ pressed, hovered }) => [
                styles.submitBtn,
                webTransition,
                (!canSubmit || sending) && styles.submitBtnDisabled,
                hovered && canSubmit && !sending && styles.submitBtnHover,
                pressed && canSubmit && { transform: [{ scale: 0.98 }] },
              ]}>
              <Send size={16} color={Palette.cream} strokeWidth={2} />
              <Text style={styles.submitBtnText}>
                {sending ? 'Sending…' : 'Send message'}
              </Text>
            </Pressable>

            {showHints && missing.length > 0 ? (
              <Text style={styles.missingNote}>
                Almost there — we still need {missing.join(', ')}.
              </Text>
            ) : null}

            <Text style={styles.footnote}>
              Sends straight to our support team — replying reaches you at
              the email above. We never share your contact info.
            </Text>
          </View>
        )}

        <Text style={styles.sectionLabel}>WHAT&apos;S COMING</Text>

        <View style={styles.roadmapCard}>
          <RoadmapRow
            Icon={HomeIcon}
            tint="#EAF0EA"
            iconColor={Palette.forest}
            title="Multi-home management"
            body="Manage a primary home, a vacation place, a rental — all in one Homestead."
          />
          <RoadmapDivider />
          <RoadmapRow
            Icon={ShieldCheck}
            tint="#DCE7E1"
            iconColor={Palette.moss}
            title="Cloud sync (opt-in)"
            body="Sign in once, see your plan on every device. Local-first stays the default."
          />
          <RoadmapDivider />
          <RoadmapRow
            Icon={Sparkles}
            tint="#F3DDAE"
            iconColor={Palette.honeyDeep}
            title="Smarter seasonal planning"
            body="Weather-aware tweaks, smarter rule timing as we learn what you actually do."
          />
        </View>

        <Text style={styles.sectionLabel}>ABOUT THIS APP</Text>

        <View style={styles.infoCard}>
          <InfoRow
            Icon={Rocket}
            label="Version"
            value={`${version.runtimeVersion} · ${version.channel}`}
          />
          <InfoRow
            Icon={Package2}
            label="Bundle"
            value={version.bundleId}
          />
          <InfoRow
            Icon={HelpCircle}
            label="Made by"
            value="A small team that loves helping people"
            multiline
          />
        </View>

        <View style={styles.footer}>
          <Heart size={12} color={Palette.honeyDeep} strokeWidth={2} />
          <Text style={styles.footerText}>
            Thanks for trying Homestead. We&apos;re grateful you&apos;re here.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/**
 * Post-submit confirmation card. Two flavors depending on whether the
 * Mailgun bridge accepted the message (server mode) or we had to fall
 * back to handing off to the user's mail client (mailto mode).
 */
function SuccessCard({
  mode,
  onReset,
}: {
  mode: 'server' | 'mailto';
  onReset: () => void;
}) {
  const isServer = mode === 'server';
  return (
    <View style={styles.successCard}>
      <View style={styles.successIcon}>
        <CheckCircle2 size={28} color={Palette.moss} strokeWidth={2} />
      </View>
      <Text style={styles.successTitle}>
        {isServer ? 'Message sent' : 'Almost there'}
      </Text>
      <Text style={styles.successBody}>
        {isServer
          ? 'Thanks for writing in. A real human reads every message — we aim to reply within a day or two.'
          : 'Your mail app should have opened with the message ready. Review it, tap send, and we’ll be in touch.'}
      </Text>
      {!isServer && Platform.OS === 'web' ? (
        <Text style={styles.successHint}>
          On a desktop without a mail client configured, your message
          might not have opened automatically. You can try the form
          again or check your default email app settings.
        </Text>
      ) : null}
      <View style={styles.successActions}>
        <Pressable
          onPress={onReset}
          style={({ pressed }) => [styles.successBtn, pressed && { opacity: 0.85 }]}
          accessibilityLabel="Send another message">
          <MailCheck size={15} color={Palette.forest} strokeWidth={2} />
          <Text style={styles.successBtnText}>Send another</Text>
        </Pressable>
      </View>
    </View>
  );
}

function RoadmapRow({
  Icon,
  tint,
  iconColor,
  title,
  body,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  tint: string;
  iconColor: string;
  title: string;
  body: string;
}) {
  return (
    <View style={styles.roadmapRow}>
      <View style={[styles.roadmapIcon, { backgroundColor: tint }]}>
        <Icon size={16} color={iconColor} strokeWidth={1.9} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.roadmapTitle}>{title}</Text>
        <Text style={styles.roadmapBody}>{body}</Text>
      </View>
    </View>
  );
}

function RoadmapDivider() {
  return <View style={styles.roadmapDivider} />;
}

function InfoRow({
  Icon,
  label,
  value,
  multiline,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <View style={styles.infoRow}>
      <View style={styles.infoIcon}>
        <Icon size={13} color={Palette.inkSoft} strokeWidth={1.9} />
      </View>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text
        style={[styles.infoValue, multiline && { flexShrink: 1, textAlign: 'right' }]}
        numberOfLines={multiline ? 2 : 1}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  topbarLeft: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  shortcutBtn: {
    width: 32, height: 32, borderRadius: 16, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: {
    fontFamily: Fonts.serif,
    fontSize: 27,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  sub: {
    marginTop: 4,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
    lineHeight: 19,
  },

  sectionLabel: {
    marginTop: Spacing.five,
    marginHorizontal: 22,
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
    marginBottom: 8,
  },

  formCard: {
    marginHorizontal: Spacing.four,
    paddingHorizontal: 16,
    paddingVertical: 18,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  fieldLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    // inkSoft (was inkFaint) — small uppercase field labels need a bit
    // more weight against the cream form card to actually read as
    // structure rather than a watermark.
    color: Palette.inkSoft,
    letterSpacing: 0.7,
    marginTop: 12,
  },
  input: {
    marginTop: 6,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  inputMultiline: {
    height: 110,
    textAlignVertical: 'top',
    lineHeight: 20,
  },
  // Gentle clay outline (never red — design principle) for fields
  // still needed after a Send attempt.
  inputInvalid: {
    borderColor: Palette.clay,
  },
  missingNote: {
    marginTop: 10,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.clay,
    lineHeight: 17,
    textAlign: 'center',
  },
  // Side-by-side container for the email + phone pair. We keep the
  // gap narrow on phones so each column still fits a useful placeholder.
  fieldRow: {
    flexDirection: 'row',
    gap: 10,
  },
  fieldRowCol: {
    flex: 1,
    minWidth: 0, // lets each TextInput shrink instead of overflow
  },

  topicRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  topicChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  topicChipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  topicChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.ink,
    letterSpacing: 0.1,
  },
  topicChipTextSel: {
    color: Palette.cream,
  },

  submitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 18,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: Palette.forest,
  },
  // Keep the button forest green even when disabled — just fade it.
  // The previous sandDeep disabled state read as a separate (broken)
  // button rather than a primary CTA waiting on input.
  submitBtnDisabled: {
    opacity: 0.45,
  },
  // Hover on the primary CTA: a hint deeper green + a subtle 1px lift.
  // The button is already weighty — we don't want it to grow on hover
  // (would feel grabby). The darker shade signals "ready to fire."
  submitBtnHover: {
    backgroundColor: '#22392B',
    transform: [{ translateY: -1 }],
  },
  submitBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14.5,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
  footnote: {
    marginTop: 10,
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkFaint,
    lineHeight: 16,
    textAlign: 'center',
  },

  successCard: {
    marginHorizontal: Spacing.four,
    paddingHorizontal: 22,
    paddingVertical: 22,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: '#C0D4B6',
    backgroundColor: '#E0EAD9',
    alignItems: 'center',
  },
  successIcon: {
    width: 52, height: 52, borderRadius: 26,
    backgroundColor: 'rgba(255,255,255,0.55)',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: 4,
  },
  successTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 19,
    color: Palette.ink,
    marginTop: 8,
  },
  successBody: {
    marginTop: 6,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 19,
    textAlign: 'center',
  },
  successHint: {
    marginTop: 8,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkFaint,
    lineHeight: 17,
    textAlign: 'center',
  },
  successActions: {
    marginTop: 16,
    flexDirection: 'row',
    gap: 10,
  },
  successBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.forest,
    backgroundColor: Palette.card,
  },
  successBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
  },

  roadmapCard: {
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  roadmapRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
  roadmapDivider: {
    height: 1,
    backgroundColor: Palette.line,
  },
  roadmapIcon: {
    width: 32, height: 32, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  roadmapTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  roadmapBody: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },

  infoCard: {
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 4,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
  },
  infoIcon: {
    width: 24, height: 24, borderRadius: 8,
    backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center',
  },
  infoLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.2,
    width: 70,
  },
  infoValue: {
    flex: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
    textAlign: 'right',
  },

  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: Spacing.five,
    marginHorizontal: Spacing.four,
  },
  footerText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    letterSpacing: 0.1,
  },
});
